import React, { useEffect, useState } from "react";
import { Box, TextField, Button, Select, MenuItem, FormControl, InputLabel, Switch, FormControlLabel, Typography, ThemeProvider, CssBaseline, Grid } from "@mui/material";

import { User } from "../user";
import { insertNewUser } from "../routes/newUser";
import darkThemeWithCssVars from "../../../views/visuals/nanesaDarkTheme";
import nanesaLightTheme from "../../../views/visuals/nanesaLightTheme";
import { updateUser } from "../routes/updateUser";

export interface UserFormProps {
  twoColumns: boolean;
  user?: User;
  refreshUserList?: () => void;
}

const UserForm: React.FC<UserFormProps> = ({ twoColumns, user, refreshUserList }) => {
  const [formData, setFormData] = useState<User | null>(null);
  const defaultUser: User = {
    firstName: "",
    lastName: "",
    email: "",
    role: 1, // Default: Admind
    company: "NANESA",
    password: undefined,
    isActive: false,
  };

  useEffect(() => {
    if (user) {
      setFormData(user);
    } else {
      setFormData(defaultUser);
    }
  }, [user]);

  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [alertColor, setAlertColor] = useState<"green" | "red" | null>(null);
  const [isTwoColumns, setIsTwoColumns] = useState<boolean>(twoColumns);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | { name?: string; value: unknown }>) => {
    const { name, value } = e.target as HTMLInputElement | HTMLTextAreaElement;
    if (name) {
      formData &&
        setFormData({
          ...formData,
          [name]: value,
        });
    }
  };

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    formData &&
      setFormData({
        ...formData,
        isActive: e.target.checked,
      });
  };

  // Regex for password validation
  const passwordValidationRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData) {
      setAlertMessage("Si è verificato un errore imprevisto.");
      setAlertColor("red");
      return;
    }

    if (!formData.id) {
      if (!passwordValidationRegex.test(formData.password!)) {
        setAlertMessage("La password deve contenere almeno 8 caratteri, una lettera maiuscola, un numero e un carattere speciale.");
        setAlertColor("red");
        return;
      }
    } else if (formData.password) {
      if (!passwordValidationRegex.test(formData.password)) {
        setAlertMessage("La password deve contenere almeno 8 caratteri, una lettera maiuscola, un numero e un carattere speciale.");
        setAlertColor("red");
        return;
      }
    }

    try {
      let res: Response = new Response();
      if (formData.id == null) {
        res = await insertNewUser(formData);
      } else {
        res = await updateUser(formData);
      }

      const data = await res.json();

      if (res.ok) {
        setAlertMessage(data.message);
        setAlertColor("green");
        refreshUserList && refreshUserList();
      } else {
        setAlertMessage(data.message);
        setAlertColor("red");
      }
    } catch (error) {
      setAlertMessage("Si è verificato un errore imprevisto.");
      setAlertColor("red");
    }

    setTimeout(() => {
      setAlertMessage(null);
      setAlertColor(null);
    }, 5000);
  };

  const savedMode = localStorage.getItem("themeMode");

  return (
    <ThemeProvider theme={savedMode === "dark" ? darkThemeWithCssVars : nanesaLightTheme}>
      <CssBaseline />
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          padding: 1,
        }}
      >
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            width: "100%",
            maxWidth: 600,
            padding: 3,
            borderRadius: 2,
            boxShadow: "none",
            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: "rgb(190,209,113)",
            },
            "& .MuiOutlinedInput-root": {
              "&.Mui-focused fieldset": {
                borderColor: "rgb(190,209,113)",
              },
            },
            "& input:-webkit-autofill": {
              WebkitBoxShadow: "0 0 0 100px rgb(190,209,113, 0.7) inset",
              WebkitTextFillColor: "#222",
              transition: "background-color 5000s ease-in-out 0s",
            },
            "& input:-webkit-autofill:focus": {
              WebkitBoxShadow: "0 0 0 100px rgb(190,209,113, 0.7) inset",
              WebkitTextFillColor: "#222",
            },
          }}
        >
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: isTwoColumns ? 6 : 12 }}>
              <TextField label="Nome" name="firstName" value={formData ? formData.firstName : ""} onChange={handleChange} fullWidth required />
            </Grid>

            <Grid size={{ xs: 12, sm: isTwoColumns ? 6 : 12 }}>
              <TextField label="Cognome" name="lastName" value={formData ? formData.lastName : ""} onChange={handleChange} fullWidth required />
            </Grid>

            <Grid size={{ xs: 12, sm: isTwoColumns ? 6 : 12 }}>
              <TextField label="Email" name="email" value={formData ? formData.email : ""} onChange={handleChange} fullWidth required type="email" />
            </Grid>

            {/* <Grid size={{ xs: 12, sm: isTwoColumns ? 6 : 12 }}>
              <FormControl fullWidth>
                <InputLabel id="role-label">Ruolo</InputLabel>
                <Select
                  labelId="role-label"
                  name="role"
                  value={formData ? formData.role : 0}
                  onChange={(e) => {
                    const { name, value } = e.target;
                    formData &&
                      setFormData({
                        ...formData,
                        [name]: value,
                      });
                  }}
                  MenuProps={{
                    disablePortal: true,
                  }}
                >
                  <MenuItem value="1">Admin</MenuItem>
                  <MenuItem value="2">User</MenuItem>
                  <MenuItem value="3">Guest</MenuItem>
                </Select>
              </FormControl>
            </Grid> */}

            <Grid size={{ xs: 12, sm: isTwoColumns ? 6 : 12 }}>
              <TextField
                label="Password"
                name="password"
                value={formData ? formData.password : ""}
                onChange={handleChange}
                fullWidth
                required={!formData}
                type="password"
                helperText="La password deve contenere almeno 8 caratteri, una lettera maiuscola, un numero e un carattere speciale."
                sx={{
                  "& .MuiInputBase-input": {
                    boxShadow: "none",
                  },
                }}
              />
            </Grid>

            {/* <Grid size={{ xs: 12, sm: isTwoColumns ? 6 : 12 }}>
              <TextField label="Azienda" name="company" value={formData ? formData.company : ""} onChange={handleChange} fullWidth required />
            </Grid> */}

            <Grid size={{ xs: 12 }}>
              <FormControlLabel control={<Switch checked={formData ? formData.isActive : false} onChange={handleSwitchChange} name="isActive" />} label={(formData ? formData.isActive : false) ? "Attivo" : "Non attivo"} />
            </Grid>
          </Grid>

          {alertMessage && (
            <Typography
              sx={{
                color: alertColor,
                textAlign: "center",
                fontWeight: "bold",
              }}
            >
              {alertMessage}
            </Typography>
          )}

          <Button type="submit" variant="contained" fullWidth>
            Salva
          </Button>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default UserForm;
